import React, {
  useState, useEffect, useCallback, useMemo, useRef,
} from 'react';
import AgentLayout from './AgentLayout';
import axios from 'axios';
import {
  Download,
  TrendingUp,
  Plane,
  Users,
  Wallet,
  CreditCard,
  ArrowUpRight,
  ArrowDownRight,
  RefreshCw,
  Building2,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
} from 'lucide-react';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from 'recharts';
import { getUserFacingBookingId } from '../../utils/userFacingBookingId';

const PIE_COLORS = ['#2563eb', '#0d9488'];

const flightGmv = (s) => s?.flight_gmv ?? s?.flight_revenue ?? 0;
const hotelGmv = (s) => s?.hotel_gmv ?? s?.hotel_revenue ?? 0;

function pad2(n) {
  return String(n).padStart(2, '0');
}

/** Naive UTC month bounds as ISO strings (aligns with backend month_utc_bounds). */
function monthUtcIsoRange(month, year) {
  const dateFrom = `${year}-${pad2(month)}-01T00:00:00`;
  const lastMs = Date.UTC(year, month, 0);
  const lastDay = new Date(lastMs).getUTCDate();
  const dateTo = `${year}-${pad2(month)}-${pad2(lastDay)}T23:59:59`;
  return { dateFrom, dateTo };
}

function yearOptions() {
  const y = new Date().getFullYear();
  return Array.from({ length: 11 }, (_, i) => y - 5 + i);
}

const AgentReports = () => {
  const [activeTab, setActiveTab] = useState('flights');
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [reportData, setReportData] = useState(null);
  const [r1Summary, setR1Summary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [flightsError, setFlightsError] = useState(null);
  const [hotelSummary, setHotelSummary] = useState(null);
  const [hotelDetail, setHotelDetail] = useState(null);
  const [txnData, setTxnData] = useState(null);
  const [hotelLoading, setHotelLoading] = useState(false);
  const [hotelsError, setHotelsError] = useState(null);

  const [txnPage, setTxnPage] = useState(1);
  const [txnLimit] = useState(20);
  const [txnVertical, setTxnVertical] = useState('all');
  const [txnSort, setTxnSort] = useState('desc');
  const [txnSearchInput, setTxnSearchInput] = useState('');
  const [txnSearchDebounced, setTxnSearchDebounced] = useState('');
  const searchDebounceRef = useRef(null);

  const [ledgerCustomRange, setLedgerCustomRange] = useState(false);
  const [ledgerDateFrom, setLedgerDateFrom] = useState('');
  const [ledgerDateTo, setLedgerDateTo] = useState('');

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const years = useMemo(() => yearOptions(), []);

  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setTxnSearchDebounced(txnSearchInput.trim());
      setTxnPage(1);
    }, 350);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [txnSearchInput]);

  const syncLedgerDefaultsToMonth = useCallback(() => {
    const { dateFrom, dateTo } = monthUtcIsoRange(month, year);
    setLedgerDateFrom(dateFrom.slice(0, 10));
    setLedgerDateTo(dateTo.slice(0, 10));
  }, [month, year]);

  useEffect(() => {
    if (!ledgerCustomRange) syncLedgerDefaultsToMonth();
  }, [month, year, ledgerCustomRange, syncLedgerDefaultsToMonth]);

  const txnQueryParams = useMemo(() => {
    const base = {
      vertical: txnVertical,
      limit: txnLimit,
      page: txnPage,
      sort: txnSort,
    };
    if (txnSearchDebounced) base.search = txnSearchDebounced;
    if (ledgerCustomRange && ledgerDateFrom && ledgerDateTo) {
      base.date_from = `${ledgerDateFrom}T00:00:00`;
      base.date_to = `${ledgerDateTo}T23:59:59`;
    } else {
      base.month = month;
      base.year = year;
    }
    return base;
  }, [
    month, year, txnPage, txnLimit, txnVertical, txnSort, txnSearchDebounced,
    ledgerCustomRange, ledgerDateFrom, ledgerDateTo,
  ]);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setFlightsError(null);
    try {
      const [monthlyRes, r1Res] = await Promise.all([
        axios.get(`${API_URL}/api/agent/dashboard/reports/monthly`, {
          withCredentials: true,
          params: { month, year },
        }),
        axios.get(`${API_URL}/api/agent/reports/summary`, {
          withCredentials: true,
          params: { month, year },
        }),
      ]);
      setReportData(monthlyRes.data);
      setR1Summary(r1Res.data);
    } catch (error) {
      console.error('Failed to fetch report:', error);
      setFlightsError(
        error.response?.data?.detail || error.message || 'Failed to load flights report',
      );
      setReportData(null);
      setR1Summary(null);
    } finally {
      setLoading(false);
    }
  }, [API_URL, month, year]);

  const loadHotelCharts = useCallback(async () => {
    setHotelLoading(true);
    setHotelsError(null);
    try {
      const [s, h] = await Promise.all([
        axios.get(`${API_URL}/api/agent/reports/summary`, { withCredentials: true, params: { month, year } }),
        axios.get(`${API_URL}/api/agent/reports/hotels`, { withCredentials: true, params: { month, year } }),
      ]);
      setHotelSummary(s.data);
      setHotelDetail(h.data);
    } catch (error) {
      console.error('Failed to fetch hotel reports:', error);
      setHotelsError(
        error.response?.data?.detail || error.message || 'Failed to load hotel reports',
      );
      setHotelSummary(null);
      setHotelDetail(null);
    } finally {
      setHotelLoading(false);
    }
  }, [API_URL, month, year]);

  const fetchTransactions = useCallback(async () => {
    const { data } = await axios.get(`${API_URL}/api/agent/reports/transactions`, {
      withCredentials: true,
      params: txnQueryParams,
    });
    setTxnData(data);
  }, [API_URL, txnQueryParams]);

  useEffect(() => {
    if (activeTab === 'flights') fetchReport();
  }, [activeTab, month, year, fetchReport]);

  useEffect(() => {
    if (activeTab !== 'hotels') return;
    loadHotelCharts();
  }, [activeTab, month, year, loadHotelCharts]);

  useEffect(() => {
    if (activeTab !== 'hotels') return;
    fetchTransactions().catch((e) => console.error('Ledger fetch failed:', e));
  }, [activeTab, fetchTransactions]);

  const refreshHotels = useCallback(() => {
    loadHotelCharts();
    fetchTransactions().catch((e) => console.error('Ledger fetch failed:', e));
  }, [loadHotelCharts, fetchTransactions]);

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];

  const handleExportFlights = () => {
    if (!reportData) return;
    const rows = [
      ['Monthly Revenue Report'],
      [`Month: ${monthNames[month - 1]} ${year}`],
      [''],
      ['Summary'],
      ['Total Bookings', reportData.summary.total_bookings],
      ['Confirmed Bookings', reportData.summary.confirmed_bookings],
      ['Total GMV', `₹${reportData.summary.total_gmv ?? reportData.summary.total_revenue}`],
      ['Total Passengers', reportData.summary.total_passengers],
      ['Wallet Credits', `₹${reportData.summary.wallet_credits}`],
      ['Wallet Debits', `₹${reportData.summary.wallet_debits}`],
      ['Wallet Payments', reportData.summary.wallet_payments],
      ['Gateway Payments', reportData.summary.gateway_payments],
    ];
    if (r1Summary?.summary) {
      const s = r1Summary.summary;
      rows.push([''], ['Combined (R1 summary)']);
      rows.push(['Flight GMV (confirmed)', flightGmv(s)]);
      rows.push(['Hotel GMV (confirmed)', hotelGmv(s)]);
      rows.push(['Flight cancellation %', s.flight_cancellation_rate]);
      rows.push(['Hotel cancellation %', s.hotel_cancellation_rate]);
      rows.push(['Combined cancellation %', s.combined_cancellation_rate]);
    }
    const csv = rows.map((row) => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `monthly-report-${year}-${month}.csv`;
    a.click();
  };

  const handleExportLedgerCsv = () => {
    const params = new URLSearchParams({ sort: 'asc' });
    if (ledgerCustomRange && ledgerDateFrom && ledgerDateTo) {
      params.set('date_from', `${ledgerDateFrom}T00:00:00`);
      params.set('date_to', `${ledgerDateTo}T23:59:59`);
    } else {
      const { dateFrom, dateTo } = monthUtcIsoRange(month, year);
      params.set('date_from', dateFrom);
      params.set('date_to', dateTo);
    }
    if (txnSearchDebounced) params.set('search', txnSearchDebounced);
    if (txnVertical !== 'all') params.set('vertical', txnVertical);
    window.open(`${API_URL}/api/agent/wallet/transactions/export?${params.toString()}`, '_blank', 'noopener,noreferrer');
  };

  const summary = hotelSummary?.summary ?? r1Summary?.summary;
  const hstats = hotelDetail?.stats;
  const r1Kpi = r1Summary?.summary ?? hotelSummary?.summary;
  const pieData = summary
    ? [
        { name: 'Flight cancellations', value: summary.flight_cancelled ?? 0 },
        { name: 'Hotel cancellations', value: summary.hotel_cancelled ?? 0 },
      ]
    : [];
  const topDestinations = hstats?.top_destinations || [];
  const topRoutesChart = (reportData?.flight_charts?.top_routes || []).map((r) => ({
    name: (r.route || 'Unknown').length > 18 ? `${(r.route || '').slice(0, 16)}…` : (r.route || 'Unknown'),
    full: r.route,
    bookings: r.bookings,
  }));
  const statusBreakdown = reportData?.flight_charts?.status_breakdown || [];
  const revenueByDay = reportData?.flight_charts?.revenue_by_day || [];

  const ledger = txnData?.ledger;
  const txnPages = txnData?.pages ?? 0;
  const txnTotal = txnData?.total ?? 0;

  const combinedKpiStrip = r1Kpi && (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 mb-6" data-testid="agent-reports-r1-strip">
      <div className="rounded-lg border border-gray-200 bg-white px-3 py-2">
        <div className="text-xs text-gray-500">Flight GMV</div>
        <div className="text-sm font-bold text-gray-900">₹{flightGmv(r1Kpi).toLocaleString()}</div>
      </div>
      <div className="rounded-lg border border-gray-200 bg-white px-3 py-2">
        <div className="text-xs text-gray-500">Hotel GMV</div>
        <div className="text-sm font-bold text-gray-900">₹{hotelGmv(r1Kpi).toLocaleString()}</div>
      </div>
      <div className="rounded-lg border border-gray-200 bg-white px-3 py-2">
        <div className="text-xs text-gray-500">Flight markup</div>
        <div className="text-sm font-bold text-gray-900">₹{(r1Kpi.flight_markup ?? 0).toLocaleString()}</div>
      </div>
      <div className="rounded-lg border border-gray-200 bg-white px-3 py-2">
        <div className="text-xs text-gray-500">Flight cancel rate</div>
        <div className="text-sm font-bold text-gray-900">{r1Kpi.flight_cancellation_rate ?? 0}%</div>
      </div>
      <div className="rounded-lg border border-gray-200 bg-white px-3 py-2">
        <div className="text-xs text-gray-500">Hotel cancel rate</div>
        <div className="text-sm font-bold text-gray-900">{r1Kpi.hotel_cancellation_rate ?? 0}%</div>
      </div>
      <div className="rounded-lg border border-teal-100 bg-teal-50 px-3 py-2">
        <div className="text-xs text-teal-800">Combined cancel rate</div>
        <div className="text-sm font-bold text-teal-900">{r1Kpi.combined_cancellation_rate ?? 0}%</div>
      </div>
    </div>
  );

  return (
    <AgentLayout>
      <div className="p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Reports</h1>
            <p className="text-gray-600 text-sm mt-1">
              Flights and hotels — performance, revenue, and wallet activity
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5" data-testid="agent-reports-tabs">
              <button
                type="button"
                onClick={() => setActiveTab('flights')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition flex items-center gap-2 ${
                  activeTab === 'flights' ? 'bg-teal-600 text-white shadow' : 'text-gray-700 hover:bg-gray-50'
                }`}
                data-testid="agent-reports-tab-flights"
              >
                <Plane className="w-4 h-4" />
                Flights
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('hotels')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition flex items-center gap-2 ${
                  activeTab === 'hotels' ? 'bg-teal-600 text-white shadow' : 'text-gray-700 hover:bg-gray-50'
                }`}
                data-testid="agent-reports-tab-hotels"
              >
                <Building2 className="w-4 h-4" />
                Hotels
              </button>
            </div>

            <select
              value={month}
              onChange={(e) => setMonth(parseInt(e.target.value, 10))}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              {monthNames.map((name, idx) => (
                <option key={name} value={idx + 1}>{name}</option>
              ))}
            </select>

            <select
              value={year}
              onChange={(e) => setYear(parseInt(e.target.value, 10))}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              {years.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>

            <button
              type="button"
              onClick={() => (activeTab === 'flights' ? fetchReport() : refreshHotels())}
              disabled={loading || hotelLoading}
              className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-5 h-5 text-gray-600 ${(activeTab === 'flights' ? loading : hotelLoading) ? 'animate-spin' : ''}`} />
            </button>

            {activeTab === 'flights' && (
              <button
                type="button"
                onClick={handleExportFlights}
                disabled={!reportData}
                className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50"
              >
                <Download className="w-4 h-4" />
                Export
              </button>
            )}

            {activeTab === 'hotels' && (
              <button
                type="button"
                onClick={handleExportLedgerCsv}
                className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
              >
                <Download className="w-4 h-4" />
                Ledger CSV
              </button>
            )}
          </div>
        </div>

        {activeTab === 'flights' && (
          <>
            {flightsError && (
              <div className="mb-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
                <span>{flightsError}</span>
              </div>
            )}
            {loading && !reportData ? (
              <div className="flex items-center justify-center py-20">
                <RefreshCw className="w-8 h-8 text-teal-600 animate-spin" />
              </div>
            ) : reportData ? (
              <>
                {combinedKpiStrip}

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl p-6 text-white">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-teal-100 text-sm font-medium">Flight GMV (month)</p>
                      <TrendingUp className="w-5 h-5 text-teal-100" />
                    </div>
                    <p className="text-3xl font-bold">₹{(reportData.summary.total_gmv ?? reportData.summary.total_revenue).toLocaleString()}</p>
                    <p className="text-xs text-teal-100 mt-1">Confirmed flights in period</p>
                  </div>

                  <div className="bg-white rounded-xl p-6 border border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-gray-600 text-sm font-medium">Total Bookings</p>
                      <Plane className="w-5 h-5 text-blue-600" />
                    </div>
                    <p className="text-3xl font-bold text-gray-900">{reportData.summary.total_bookings}</p>
                    <p className="text-xs text-green-600 mt-1">
                      {reportData.summary.confirmed_bookings} confirmed
                    </p>
                  </div>

                  <div className="bg-white rounded-xl p-6 border border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-gray-600 text-sm font-medium">Total Passengers</p>
                      <Users className="w-5 h-5 text-purple-600" />
                    </div>
                    <p className="text-3xl font-bold text-gray-900">{reportData.summary.total_passengers}</p>
                  </div>

                  <div className="bg-white rounded-xl p-6 border border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-gray-600 text-sm font-medium">Wallet Activity</p>
                      <Wallet className="w-5 h-5 text-orange-600" />
                    </div>
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-sm">
                        <ArrowUpRight className="w-4 h-4 text-green-600" />
                        <span className="text-gray-600">Credits:</span>
                        <span className="font-bold text-green-600">
                          ₹{reportData.summary.wallet_credits.toLocaleString()}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <ArrowDownRight className="w-4 h-4 text-red-600" />
                        <span className="text-gray-600">Debits:</span>
                        <span className="font-bold text-red-600">
                          ₹{reportData.summary.wallet_debits.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {(statusBreakdown.length > 0 || revenueByDay.length > 0 || topRoutesChart.length > 0) && (
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
                    {statusBreakdown.length > 0 && (
                      <div className="bg-white rounded-xl p-4 border border-gray-200">
                        <h3 className="text-sm font-bold text-gray-900 mb-2">Bookings by status</h3>
                        <div className="h-52 min-h-[200px] w-full min-w-0">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={statusBreakdown} layout="vertical" margin={{ left: 8 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                              <XAxis type="number" allowDecimals={false} tick={{ fontSize: 10 }} />
                              <YAxis type="category" dataKey="status" width={72} tick={{ fontSize: 10 }} />
                              <Tooltip />
                              <Bar dataKey="count" fill="#2563eb" name="Bookings" />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    )}
                    {revenueByDay.length > 0 && (
                      <div className="bg-white rounded-xl p-4 border border-gray-200 lg:col-span-1">
                        <h3 className="text-sm font-bold text-gray-900 mb-2">Confirmed GMV by day</h3>
                        <div className="h-52 min-h-[200px] w-full min-w-0">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={revenueByDay}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                              <XAxis dataKey="day" tick={{ fontSize: 10 }} />
                              <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `₹${v}`} />
                              <Tooltip formatter={(v) => [`₹${Number(v).toLocaleString()}`, 'GMV']} />
                              <Bar dataKey="revenue" fill="#0d9488" name="GMV" />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    )}
                    {topRoutesChart.length > 0 && (
                      <div className="bg-white rounded-xl p-4 border border-gray-200">
                        <h3 className="text-sm font-bold text-gray-900 mb-2">Top routes</h3>
                        <div className="h-52 min-h-[200px] w-full min-w-0">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={topRoutesChart} layout="vertical" margin={{ left: 4 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                              <XAxis type="number" allowDecimals={false} tick={{ fontSize: 10 }} />
                              <YAxis type="category" dataKey="name" width={56} tick={{ fontSize: 9 }} />
                              <Tooltip formatter={(v, _n, item) => [v, item?.payload?.full || 'Route']} />
                              <Bar dataKey="bookings" fill="#7c3aed" name="Bookings" />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="bg-white rounded-xl p-6 border border-gray-200 mb-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Payment Method Breakdown</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center justify-between p-4 bg-teal-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Wallet className="w-8 h-8 text-teal-600" />
                        <div>
                          <p className="text-sm text-gray-600">Wallet Payments</p>
                          <p className="text-2xl font-bold text-gray-900">{reportData.summary.wallet_payments}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-500">Percentage</p>
                        <p className="text-lg font-bold text-teal-600">
                          {reportData.summary.total_bookings > 0
                            ? Math.round((reportData.summary.wallet_payments / reportData.summary.total_bookings) * 100)
                            : 0}%
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <CreditCard className="w-8 h-8 text-blue-600" />
                        <div>
                          <p className="text-sm text-gray-600">Gateway Payments</p>
                          <p className="text-2xl font-bold text-gray-900">{reportData.summary.gateway_payments}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-500">Percentage</p>
                        <p className="text-lg font-bold text-blue-600">
                          {reportData.summary.total_bookings > 0
                            ? Math.round((reportData.summary.gateway_payments / reportData.summary.total_bookings) * 100)
                            : 0}%
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <div className="p-6 border-b border-gray-200 flex flex-wrap items-center justify-between gap-2">
                    <h3 className="text-lg font-bold text-gray-900">Bookings This Month</h3>
                    {reportData.bookings_total != null && (
                      <span className="text-xs text-gray-500">
                        Showing {reportData.bookings.length} of {reportData.bookings_total}
                      </span>
                    )}
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Booking ID</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Passengers</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {(reportData.bookings || []).map((booking, idx) => (
                          <tr key={booking.booking_id || booking.id || idx} className="hover:bg-gray-50">
                            <td className="px-6 py-4 text-sm font-medium text-gray-900">
                              {getUserFacingBookingId(booking)}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-600">
                              {booking.created_at ? new Date(booking.created_at).toLocaleDateString() : '—'}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-600">
                              {booking.total_passengers}
                            </td>
                            <td className="px-6 py-4 text-sm font-semibold text-gray-900">
                              ₹{booking.total_amount?.toLocaleString() || '0'}
                            </td>
                            <td className="px-6 py-4">
                              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                                booking.payment_method === 'wallet'
                                  ? 'bg-teal-100 text-teal-700'
                                  : 'bg-blue-100 text-blue-700'
                              }`}
                              >
                                {booking.payment_method === 'wallet' ? (
                                  <><Wallet className="w-3 h-3" /> Wallet</>
                                ) : (
                                  <><CreditCard className="w-3 h-3" /> Gateway</>
                                )}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                                String(booking.status || '').toLowerCase() === 'confirmed'
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-yellow-100 text-yellow-700'
                              }`}
                              >
                                {booking.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-20 text-gray-500">
                No data available for selected period
              </div>
            )}
          </>
        )}

        {activeTab === 'hotels' && (
          <>
            {hotelsError && (
              <div className="mb-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
                <span>{hotelsError}</span>
              </div>
            )}
            {hotelLoading && !hotelSummary ? (
              <div className="flex items-center justify-center py-20">
                <RefreshCw className="w-8 h-8 text-teal-600 animate-spin" />
              </div>
            ) : summary && hstats ? (
              <>
                {combinedKpiStrip}

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  <div className="bg-white rounded-xl p-6 border border-gray-200" data-testid="kpi-hotel-bookings">
                    <p className="text-gray-600 text-sm font-medium mb-2">Total Hotel Bookings</p>
                    <p className="text-3xl font-bold text-gray-900">{summary.hotel_count}</p>
                    <p className="text-xs text-gray-500 mt-1">Created this month (all statuses)</p>
                  </div>
                  <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl p-6 text-white">
                    <p className="text-teal-100 text-sm font-medium mb-2">Hotel GMV</p>
                    <p className="text-3xl font-bold">₹{hotelGmv(summary).toLocaleString()}</p>
                    <p className="text-xs text-teal-100 mt-1">Confirmed only</p>
                  </div>
                  <div className="bg-white rounded-xl p-6 border border-gray-200" data-testid="kpi-avg-nights">
                    <p className="text-gray-600 text-sm font-medium mb-2">Avg Nights / Booking</p>
                    <p className="text-3xl font-bold text-gray-900">{hstats.avg_nights_per_booking}</p>
                    <p className="text-xs text-gray-500 mt-1">Confirmed stays</p>
                  </div>
                  <div className="bg-white rounded-xl p-6 border border-gray-200" data-testid="kpi-top-cities">
                    <p className="text-gray-600 text-sm font-medium mb-2">Top city</p>
                    <p className="text-xl font-bold text-gray-900">
                      {topDestinations[0]?.city || '—'}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {topDestinations[0] ? `${topDestinations[0].bookings} bookings` : 'No confirmed stays'}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mb-6">
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                    <div className="text-gray-500 text-xs">Flight GMV (confirmed)</div>
                    <div className="font-bold text-gray-900">₹{flightGmv(summary).toLocaleString()}</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                    <div className="text-gray-500 text-xs">Flight markup</div>
                    <div className="font-bold text-gray-900">₹{(summary.flight_markup ?? 0).toLocaleString()}</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                    <div className="text-gray-500 text-xs">Combined cancel rate</div>
                    <div className="font-bold text-gray-900">{summary.combined_cancellation_rate ?? 0}%</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                    <div className="text-gray-500 text-xs">Flight cancel rate</div>
                    <div className="font-bold text-gray-900">{summary.flight_cancellation_rate ?? 0}%</div>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
                  <div className="bg-white rounded-xl p-6 border border-gray-200">
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Cancellation breakdown</h3>
                    <p className="text-xs text-gray-500 mb-4">Count of cancelled bookings this month (not rate)</p>
                    {pieData.some((d) => d.value > 0) ? (
                      <div className="h-64 min-h-[240px] w-full min-w-0" data-testid="chart-cancellations-pie">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={88}>
                              {pieData.map((_, i) => (
                                <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip />
                            <Legend />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 py-8 text-center">No cancellations in this month</p>
                    )}
                  </div>

                  <div className="bg-white rounded-xl p-6 border border-gray-200">
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Check-in day of week</h3>
                    <p className="text-xs text-gray-500 mb-4">Confirmed hotel bookings by weekday of check-in</p>
                    <div className="h-64 min-h-[240px] w-full min-w-0">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={hstats.checkin_day_of_week || []}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                          <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                          <Tooltip />
                          <Bar dataKey="bookings" fill="#0d9488" name="Bookings" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>

                {topDestinations.length > 0 && (
                  <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-6">
                    <div className="p-4 border-b border-gray-200">
                      <h3 className="text-lg font-bold text-gray-900">Top destinations (confirmed)</h3>
                      <p className="text-xs text-gray-500 mt-1">Up to 10 cities by booking count</p>
                    </div>
                    <div className="h-56 min-h-[220px] w-full min-w-0 p-2">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={topDestinations.map((c) => ({ ...c, label: c.city }))} layout="vertical" margin={{ left: 12 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis type="number" allowDecimals={false} tick={{ fontSize: 10 }} />
                          <YAxis type="category" dataKey="label" width={100} tick={{ fontSize: 10 }} />
                          <Tooltip />
                          <Bar dataKey="bookings" fill="#0f766e" name="Bookings" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}

                <div className="rounded-xl border border-gray-200 bg-white p-4 mb-4 space-y-3">
                  <div className="flex flex-wrap items-center gap-3">
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={ledgerCustomRange}
                        onChange={(e) => {
                          setLedgerCustomRange(e.target.checked);
                          setTxnPage(1);
                        }}
                        data-testid="ledger-custom-range"
                      />
                      Custom ledger period
                    </label>
                    {ledgerCustomRange && (
                      <>
                        <input
                          type="date"
                          value={ledgerDateFrom}
                          onChange={(e) => { setLedgerDateFrom(e.target.value); setTxnPage(1); }}
                          className="px-2 py-1 border border-gray-300 rounded text-sm"
                          data-testid="ledger-date-from"
                        />
                        <span className="text-gray-400">to</span>
                        <input
                          type="date"
                          value={ledgerDateTo}
                          onChange={(e) => { setLedgerDateTo(e.target.value); setTxnPage(1); }}
                          className="px-2 py-1 border border-gray-300 rounded text-sm"
                          data-testid="ledger-date-to"
                        />
                      </>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2 items-center">
                    <select
                      value={txnVertical}
                      onChange={(e) => { setTxnVertical(e.target.value); setTxnPage(1); }}
                      className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
                      data-testid="txn-vertical-filter"
                    >
                      <option value="all">All verticals</option>
                      <option value="flight">Flight</option>
                      <option value="hotel">Hotel</option>
                    </select>
                    <select
                      value={txnSort}
                      onChange={(e) => { setTxnSort(e.target.value); setTxnPage(1); }}
                      className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
                      data-testid="txn-sort"
                    >
                      <option value="desc">Newest first</option>
                      <option value="asc">Oldest first</option>
                    </select>
                    <input
                      type="search"
                      placeholder="Search ledger…"
                      value={txnSearchInput}
                      onChange={(e) => setTxnSearchInput(e.target.value)}
                      className="flex-1 min-w-[160px] px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
                      data-testid="txn-search"
                    />
                  </div>
                </div>

                {ledger && (
                  <div
                    className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 mb-4 text-sm"
                    data-testid="agent-reports-ledger-summary"
                  >
                    <div className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
                      <div className="text-xs text-gray-500">Opening balance</div>
                      <div className="font-semibold">₹{Number(ledger.opening_balance ?? 0).toLocaleString()}</div>
                    </div>
                    <div className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
                      <div className="text-xs text-gray-500">Closing balance</div>
                      <div className="font-semibold">₹{Number(ledger.closing_balance ?? 0).toLocaleString()}</div>
                    </div>
                    <div className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
                      <div className="text-xs text-gray-500">Window credits</div>
                      <div className="font-semibold text-green-700">₹{Number(ledger.total_credit ?? 0).toLocaleString()}</div>
                    </div>
                    <div className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
                      <div className="text-xs text-gray-500">Window debits</div>
                      <div className="font-semibold text-red-700">₹{Number(ledger.total_debit ?? 0).toLocaleString()}</div>
                    </div>
                    <div className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
                      <div className="text-xs text-gray-500">Net change</div>
                      <div className="font-semibold">₹{Number(ledger.net_change ?? 0).toLocaleString()}</div>
                    </div>
                    <div className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
                      <div className="text-xs text-gray-500">Txn count</div>
                      <div className="font-semibold">{ledger.txn_count ?? 0}</div>
                    </div>
                  </div>
                )}

                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-6">
                  <div className="p-6 border-b border-gray-200 flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <Wallet className="w-5 h-5 text-teal-600" />
                      <h3 className="text-lg font-bold text-gray-900">Wallet ledger (flight + hotel)</h3>
                    </div>
                    <span className="text-xs text-gray-500">{txnTotal} total</span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                        <tr>
                          <th className="px-4 py-2 text-left">Date</th>
                          <th className="px-4 py-2 text-left">Type</th>
                          <th className="px-4 py-2 text-left">Vertical</th>
                          <th className="px-4 py-2 text-left">Description</th>
                          <th className="px-4 py-2 text-right">Debit</th>
                          <th className="px-4 py-2 text-right">Credit</th>
                          <th className="px-4 py-2 text-right">Balance</th>
                          <th className="px-4 py-2 text-left">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {(txnData?.transactions || []).map((tx, idx) => (
                          <tr key={tx.transaction_id || idx} className="hover:bg-gray-50">
                            <td className="px-4 py-2 whitespace-nowrap text-gray-600">
                              {tx.created_at ? String(tx.created_at).slice(0, 16).replace('T', ' ') : '—'}
                            </td>
                            <td className="px-4 py-2 font-medium capitalize">{tx.type}</td>
                            <td className="px-4 py-2">
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                tx.vertical === 'hotel' ? 'bg-teal-100 text-teal-800' : 'bg-blue-100 text-blue-800'
                              }`}
                              >
                                {tx.vertical}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-gray-700 max-w-xs truncate" title={tx.description}>{tx.description}</td>
                            <td className="px-4 py-2 text-right text-red-600">
                              {tx.debit_amount ? `₹${Number(tx.debit_amount).toLocaleString()}` : '—'}
                            </td>
                            <td className="px-4 py-2 text-right text-green-600">
                              {tx.credit_amount ? `₹${Number(tx.credit_amount).toLocaleString()}` : '—'}
                            </td>
                            <td className="px-4 py-2 text-right text-gray-800">
                              {tx.balance != null ? `₹${Number(tx.balance).toLocaleString()}` : '—'}
                            </td>
                            <td className="px-4 py-2 text-gray-600 text-xs">{tx.status || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {(!txnData?.transactions || txnData.transactions.length === 0) && (
                      <div className="p-8 text-center text-gray-500 text-sm">No wallet transactions for this filter</div>
                    )}
                  </div>
                  {txnPages > 1 && (
                    <div className="flex items-center justify-center gap-2 py-3 border-t border-gray-100">
                      <button
                        type="button"
                        disabled={txnPage <= 1}
                        onClick={() => setTxnPage((p) => Math.max(1, p - 1))}
                        className="p-2 rounded border border-gray-200 disabled:opacity-40"
                        data-testid="txn-page-prev"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      <span className="text-sm text-gray-600" data-testid="txn-page-label">
                        Page {txnPage} / {txnPages}
                      </span>
                      <button
                        type="button"
                        disabled={txnPage >= txnPages}
                        onClick={() => setTxnPage((p) => Math.min(txnPages, p + 1))}
                        className="p-2 rounded border border-gray-200 disabled:opacity-40"
                        data-testid="txn-page-next"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                    <div className="text-gray-500 text-xs">Hotel markup</div>
                    <div className="font-bold text-gray-900">₹{(summary.hotel_markup ?? 0).toLocaleString()}</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                    <div className="text-gray-500 text-xs">ADR (confirmed)</div>
                    <div className="font-bold text-gray-900">₹{hstats.adr}</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                    <div className="text-gray-500 text-xs">Hotel cancellation rate</div>
                    <div className="font-bold text-gray-900">{summary.hotel_cancellation_rate}%</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                    <div className="text-gray-500 text-xs">Confirmed room-nights</div>
                    <div className="font-bold text-gray-900">{hstats.total_room_nights ?? '—'}</div>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-20 text-gray-500">
                No hotel report data for this period
              </div>
            )}
          </>
        )}
      </div>
    </AgentLayout>
  );
};

export default AgentReports;
